/* External */
import { useMemo, memo } from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import Icon from '@dtvgo/icon';
import CardEpisodeCommon from '@dtvgo/card-episode';

/* Components */
import Progress from './Progress';

/* Utils */
import { ImageRatio } from '../../utils/constants';
import { getIconFromBlockStatus } from '../../utils/iconBlockUtils';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';
import {
  getFormattedDuration,
  useFullEpisodeName,
  getRating,
  joinArrayWithSpace,
} from '../../utils/metaDataUtils';
import { Content } from '../../storage/models';

/* Hooks */
import useImageCard from '../../hooks/useImageCard';

/* Styles */
import './CardEpisode.scss';

const IMG_WIDTH = 405;
const IMG_DIMENSION = ImageRatio.FOUR_BY_THREE;

function CardEpisode({
  className = '',
  focused,
  content,
  onMouseDown = undefined,
  onMouseEnter = undefined,
}) {
  const {
    title,
    description,
    duration,
    progress,
    images,
  } = content || {};

  const showLockIcon = useLockIcon(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);

  const iconName = getIconFromBlockStatus(showLockIcon, isContentBlockedByParentalControl);

  const titleEpisode = useFullEpisodeName(content);

  const subtitle = useMemo(() => joinArrayWithSpace([
    getRating(content),
    getFormattedDuration(content),
  ]), [content]);

  const { imageUrl } = useImageCard({
    images,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  const progressOffset = useMemo(() => {
    if (progress >= duration * 0.95) {
      return 0;
    }

    return progress;
  }, [duration, progress]);

  return (
    <CardEpisodeCommon
      className={`dtv-html5-c-card-episode ${focused ? 'dtv-html5-c-card-episode--focused' : ''} ${className}`}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <CardEpisodeCommon.CardImage
        className={`dtv-html5-c-card-episode__container-image ${progressOffset > 0 ? 'dtv-html5-c-card-episode__container-image--background-filter' : ''}`}
        imageUrl={imageUrl}
        imageAlt={title || ''}
      >
        {showLockIcon && <CardEpisodeCommon.Lock className="dtv-html5-c-card-episode__lock" />}
        {(isContentBlockedByParentalControl && !showLockIcon) && (
          <div className="dtv-html5-c-card-episode__parental-control-block">
            <Icon name="parental-block" className="dtv-html5-c-card-episode__parental-control-icon" />
          </div>
        )}
        <div className={`dtv-html5-c-card-episode__icon-container ${focused ? 'dtv-html5-c-card-episode__icon-container--focused' : ''}`}>
          <Icon
            name={iconName}
            className="dtv-html5-c-card-episode__icon"
          />
        </div>
        {progressOffset > 0 && (
          <Progress
            progress={progressOffset}
            duration={duration}
          />
        )}
      </CardEpisodeCommon.CardImage>
      <div className="dtv-html5-c-card-episode__metadata">
        <CardEpisodeCommon.Title
          className="dtv-html5-c-card-episode__title"
        >
          {titleEpisode || ''}
        </CardEpisodeCommon.Title>
        <CardEpisodeCommon.Subtitle>
          <span className="dtv-html5-c-card-episode__subtitle">{subtitle}</span>
        </CardEpisodeCommon.Subtitle>
        <CardEpisodeCommon.Description
          className="dtv-html5-c-card-episode__description"
          text={description || ''}
        />
      </div>
    </CardEpisodeCommon>
  );
}

CardEpisode.propTypes = {
  className: PropTypes.string,
  focused: PropTypes.bool.isRequired,
  content: PropTypes.instanceOf(Content).isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

export default memo(withInteractiveFocusable()(memo(CardEpisode)));
